import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { handleRequestError } from 'Shared/utils/handle-request-error';
import { fetchActionItems, createItem, nestItem } from 'Shared/api/items';
import { ActionItemsContext } from 'Shared/api/types';
import { ItemType } from 'Shared/constants/types';
import { handleError, camelToSnakeCase } from 'Shared/helpers';
import { generateTempItem, generateFormDataFromItem } from 'Shared/helpers/items';
import { getSourcePage } from 'Shared/utils/getSourcePage';
import { getActionItems, getDiscussionTopics } from 'Shared/selectors/meetingItems';
import { sortByHighPriority } from 'Shared/utils/sorting';

import { getUuidsToPositions } from './utils';
import { getMeetingItemByUuid } from '../selectors/common';

export function fetchActionitemsAction({ context = ActionItemsContext.me, status = 'all', ...additionalParams }) {
  return (dispatch, getState) => {
    const { app } = getState();

    return handleRequestError(fetchActionItems({ context, status, appId: app.id, ...additionalParams })).then(
      response => {
        const data = response?.data?.data;
        dispatch({
          type: 'GET_MEETING_ITEMS_RESPONSE',
          payload: data,
        });

        return data;
      },
      error => {
        dispatch(handleError(error));
      }
    );
  };
}

export function fetchAdditionalActionitemsAction({
  context = ActionItemsContext.assignedTo,
  status = 'all',
  ...additionalParams
}) {
  return (dispatch, getState) => {
    const { app } = getState();

    return handleRequestError(fetchActionItems({ context, status, appId: app.id, ...additionalParams })).then(
      response => {
        const data = response?.data?.data;
        dispatch({
          type: 'MEETING_ITEMS_SYNC',
          meetingItems: data?.actionItems,
        });
        return data;
      },
      error => {
        dispatch(handleError(error));
      }
    );
  };
}

const SourceItemWhiteList = Object.freeze([
  'assignee',
  'assigner',
  'assignmentAvatar',
  'assignmentId',
  'assignmentPersonId',
  'itemType',
  'parentText',
  'parentUuid',
  'parentId',
  'workspaceId',
  'isHighPriority',
  'user',
  'positionForActionItemsPage',
  'positionForGoalDetailPage',
  'positionForHomePage',
  'position',
  'goalSlug',
  'goalId',
  'reviewSlug',
]);

export function createItemFromSourceItemAction({ sourceItemUuid, itemUuid, positionField = 'position', ...params }) {
  return async (dispatch, getState) => {
    const item = getMeetingItemByUuid(sourceItemUuid)(getState());
    const copyData = pick(item, SourceItemWhiteList);

    let assignmentInfo = {};
    if (params.isAssignCurrentUser === false) {
      assignmentInfo = {
        assigneePersonId: copyData.assignmentPersonId,
        assigneeAvatar: copyData.assignmentAvatar,
      };
    }

    const user = getState().user;
    const tempItem = generateTempItem({
      ...copyData,
      itemUuid,
      user,
      assignmentInfo,
      [positionField]: copyData[positionField] + 1,
      ...params,
    });

    dispatch({
      type: 'MEETING_ITEM_ADD_REQUEST',
      positionField: 'positionForHomePage',
      meetingItemType: tempItem.itemType,
      payload: tempItem,
    });
    const data = generateFormDataFromItem(tempItem);

    data.append('clientSyncToken', getState().app.clientSyncToken);
    return handleRequestError(createItem(data, getSourcePage()))
      .then(response => {
        dispatch({
          type: 'MEETING_ITEM_ADD_RESPONSE',
          meetingItemType: tempItem.itemType,
          payload: response.data.data,
        });
        return response.data;
      })
      .catch(error => {
        WpToastManager.danger(error.message);
        dispatch({ type: 'MEETING_ITEM_ADD_ERROR', payload: { uuid: itemUuid } });
      });
  };
}

export function nestItemAction({ draggedItemUuids, parentId, parentUuid, itemType, positionField }) {
  return (dispatch, getState) => {
    dispatch({
      type: 'MEETING_ITEM_NEST_REQUEST',
      payload: {
        draggedItemUuids,
        parentId,
        parentUuid,
        itemType,
        positionField,
      },
    });
    const state = getState();
    const data = {
      draggedItemUuids,
      parentUuid,
      uuidsToPositions: {
        discussionTopics: getUuidsToPositions(getDiscussionTopics(state)),
        actionItems: getUuidsToPositions(getActionItems(state)),
      },
      positionField: camelToSnakeCase(positionField),
      clientSyncToken: state.app.clientSyncToken,
    };

    const nestItemRequestFunction = () =>
      handleRequestError(nestItem(data)).catch(error => {
        WpToastManager.danger(error.message);
      });

    // This happens when tabbing to nest before the create request succeeded. Delaying
    // should ensure they get processed in the correct order but it feels like there's a
    // more robust way to solve this in the future.
    if (state.meetingItems.some(({ uuid, id }) => draggedItemUuids.includes(uuid) && !id))
      return setTimeout(nestItemRequestFunction, 1000);

    return nestItemRequestFunction();
  };
}

export const nestByTabAction =
  ({ uuid, itemType, positionField }) =>
  (dispatch, getState) => {
    const state = getState();
    const items =
      ItemType.actionItem === itemType ? sortByHighPriority(getActionItems(state)) : getDiscussionTopics(state);
    const sortedItems = sortBy(items, [positionField]);

    const todo = sortedItems.find(t => t.uuid === uuid);
    const childPresented = Boolean(
      sortedItems.filter(({ parentUuid: itemParentUuid }) => itemParentUuid === todo.uuid).length
    );
    const index = sortedItems.indexOf(todo);
    if (index === 0 || childPresented || todo.parentUuid) return;
    const parentList = sortedItems.filter(({ parentUuid: itemParentUuid }) => !itemParentUuid);
    const prevParentItem = parentList[parentList.indexOf(todo) - 1];

    dispatch(
      nestItemAction({
        draggedItemUuids: [todo.uuid],
        parentId: prevParentItem.id,
        parentUuid: prevParentItem.uuid,
        itemType,
        positionField,
      })
    );
  };
